// Lesson-context extraction for Attune (Phase 0, Foundation B).
//
// An Attune moment needs to know what surrounds it in the lesson — the slides
// *before* it (GYD facilitator: progressive context — only what the learner has
// seen), the slides *after* it (Hook: what the opener leads into), or the *whole*
// lesson (Experience: the full picture). This walks a lesson's `version: 2`
// slides and emits readable text per slice.
//
// Why not reuse `lib/ccExport/slideToHtml.ts`: `slideToHtmlFragment` only covers
// seven legacy templates and returns '' for everything else (quiz, webapp, and
// all the scenario interaction types), so most slides would contribute nothing.
// Instead we walk each slide's data generically, collecting human-readable copy
// under a whitelist of content keys — robust across every template, old and new.

import type {
  GeneratedInteractiveSlideData,
  Slide,
  SlideBasedContent,
} from '../components/course-builder/slides/slideTypes';
import { DOORS } from './doors/doors';

export type LessonContextScope = 'before' | 'after' | 'whole';

export interface LessonContext {
  lessonTitle?: string;
  /** Readable text of slides before the moment. Empty if it's the first slide. */
  before: string;
  /** Readable text of slides after the moment. Empty if it's the last slide. */
  after: string;
  /** Readable text of the whole lesson (the moment slide itself excluded). */
  whole: string;
}

// Object keys whose string values are learner-facing slide copy worth feeding to
// the model. Deliberately excludes ids, urls, colours, and answer-key fields
// (feedback/correct/answer) so the context is "what the learner saw", not a
// solutions leak.
const CONTENT_KEYS = new Set([
  'title', 'heading', 'subheading', 'subtitle',
  'body', 'text', 'content', 'description', 'caption',
  'question', 'prompt', 'statement', 'claim',
  'label', 'instruction', 'summary',
]);

function stripHtml(value: string): string {
  return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

// Recursively collect readable strings. Array items inherit their parent key, so
// option/item objects are visited and their own content keys (e.g. `text`) match.
function collect(value: unknown, key: string | null, out: string[]): void {
  if (value == null) return;
  if (typeof value === 'string') {
    if (key && CONTENT_KEYS.has(key.toLowerCase())) {
      const text = stripHtml(value);
      if (text) out.push(text);
    }
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) collect(item, key, out);
    return;
  }
  if (typeof value === 'object') {
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      collect(v, k, out);
    }
  }
}

/**
 * One slide → a compact readable line prefixed with its template, or '' if the
 * slide carries no learner-facing copy (e.g. a bare media slide).
 */
export function slideToText(slide: Slide): string {
  if (slide.data.template === 'generated-interactive') return doorToText(slide.data);
  const data = slide.data as unknown as Record<string, unknown>;
  const out: string[] = [];
  for (const [k, v] of Object.entries(data)) {
    if (k === 'template') continue;
    collect(v, k, out);
  }
  const text = out.join(' · ');
  return text ? `[${String(data.template)}] ${text}` : '';
}

/**
 * An Activity Door, as Attune should know it. The generic walk finds nothing
 * here: the brief lives under `intake.context` / `intake.goal` (not content
 * keys) and the build is one sealed HTML string, so a door used to contribute
 * an empty line, and a facilitator placed after a scenario never knew one had
 * happened. The author's brief is the right thing to hand over: it says what
 * the activity is about and what it is for. The built HTML deliberately stays
 * out, because it carries every branch and every piece of feedback, which is
 * exactly the "not what the learner saw" material CONTENT_KEYS exists to keep
 * away from the model.
 */
function doorToText(data: GeneratedInteractiveSlideData): string {
  const name = DOORS.find((d) => d.key === data.door)?.name ?? 'Activity';
  const context = stripHtml(data.intake?.context ?? '');
  const goal = stripHtml(data.intake?.goal ?? '');
  if (!context && !goal) return '';
  const parts = [`An interactive ${name} activity the learner plays through`];
  if (context) parts.push(`What it is about: ${context}`);
  if (goal) parts.push(`What it is meant to achieve: ${goal}`);
  const invented = stripHtml(data.receipt ?? '');
  if (invented) parts.push(`Details invented for it: ${invented}`);
  return `[${name}] ${parts.join(' · ')}`;
}

/**
 * Build readable lesson context relative to a moment slide.
 *
 * `momentIndex` is the Attune slide's index within `content.slides`; it is
 * excluded from every slice (it has no learner-facing copy of its own). The
 * `before` slice is the load-bearing guarantee for the GYD facilitator: a moment
 * at index N never sees text from slides after N.
 */
export function buildLessonContext(
  content: SlideBasedContent,
  momentIndex: number,
  opts: { lessonTitle?: string } = {},
): LessonContext {
  // Sort by `order` first: callers compute momentIndex against the order-sorted
  // view of the lesson (the viewer's `slides`, the builder's `selectedIndex`), so
  // we must slice the same order — otherwise before/after/whole are computed at
  // the wrong slide when the stored array order differs from the `order` fields.
  const slides = [...(content.slides ?? [])].sort((a, b) => a.order - b.order);
  const render = (list: Slide[]): string =>
    list.map(slideToText).filter(Boolean).join('\n');

  const before = render(slides.slice(0, Math.max(0, momentIndex)));
  const after = render(slides.slice(momentIndex + 1));
  const whole = render(slides.filter((_, i) => i !== momentIndex));

  return { lessonTitle: opts.lessonTitle, before, after, whole };
}

export type AttuneAutoMode = 'facilitator' | 'experience';

/**
 * Resolve an Attune slide's position-based mode when the author left it on
 * "Auto": the end-of-lesson send-off (experience) on the last slide,
 * facilitator everywhere else — including the start. A cold diagnostic makes a
 * poor lesson opener, so Auto never opens a lesson with one; a designer who
 * wants early engagement pins a facilitator (or builds a hook interactive
 * themselves). `index` is the moment's index in the order-sorted lesson.
 */
export function resolveAttuneAutoMode(slides: Slide[], index: number): AttuneAutoMode {
  return index >= slides.length - 1 ? 'experience' : 'facilitator';
}

/** Convenience: the single slice a given mode needs. */
export function lessonContextFor(
  content: SlideBasedContent,
  momentIndex: number,
  scope: LessonContextScope,
  opts: { lessonTitle?: string } = {},
): string {
  return buildLessonContext(content, momentIndex, opts)[scope];
}

// ── The facilitator chip's context phrase ────────────────────────────────────
// "A moment with Attune · about what you just read" was hardcoded — wrong after
// a video, an interactive, or a webapp. Derive the verb from what the previous
// slide actually was; unknown templates default to "did" (new templates are
// almost always hands-on interactions).
const CHIP_READ = new Set(['text', 'cover', 'code', 'bullets', 'text-block', 'content', 'split', 'image-text']);
const CHIP_WATCHED = new Set(['video', 'media']);
const CHIP_EXPLORED = new Set(['webapp', 'canva', 'guided-sandbox']);

export function attuneChipContext(prevTemplate?: string | null): string {
  if (!prevTemplate) return 'about this lesson';
  if (CHIP_READ.has(prevTemplate)) return 'about what you just read';
  if (CHIP_WATCHED.has(prevTemplate)) return 'about what you just watched';
  if (prevTemplate === 'image') return 'about what you just saw';
  if (CHIP_EXPLORED.has(prevTemplate)) return 'about what you just explored';
  return 'about what you just did';
}
